import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getMe, logout as logoutService, getStoredToken, refreshToken } from '../services/authService';

const AuthContext = createContext(null);

const TOKEN_REFRESH_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour

// Safari-safe localStorage helpers
function lsRemove(key) { try { localStorage.removeItem(key); } catch {} }
function lsSet(key, val) { try { localStorage.setItem(key, val); } catch {} }

function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const scheduleTokenRefresh = useCallback((currentToken) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const expiry = getTokenExpiry(currentToken);
    if (!expiry) return;
    const timeUntilExpiry = expiry - Date.now();
    const refreshIn = timeUntilExpiry - TOKEN_REFRESH_THRESHOLD_MS;
    if (refreshIn > 0) {
      refreshTimerRef.current = setTimeout(async () => {
        try {
          const newToken = await refreshToken();
          if (newToken) {
            setToken(newToken);
            scheduleTokenRefresh(newToken);
          }
        } catch {
          // Refresh failed — let user stay logged in until next API call triggers 401
        }
      }, refreshIn);
    } else if (timeUntilExpiry > 0) {
      // Token expires within threshold — refresh now
      refreshToken().then((newToken) => {
        if (newToken) {
          setToken(newToken);
          scheduleTokenRefresh(newToken);
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = getStoredToken();
      if (!storedToken) {
        setIsLoading(false);
        return;
      }
      setToken(storedToken);
      try {
        const userData = await getMe();
        setUser(userData);
        scheduleTokenRefresh(storedToken);
      } catch {
        lsRemove('auth_token');
        lsRemove('auth_refresh_token');
        lsRemove('auth_user');
        setToken(null);
        setUser(null);
        // ProtectedRoute will redirect to /login automatically when isAuthenticated is false
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [scheduleTokenRefresh]);

  const login = useCallback((userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    if (authToken) scheduleTokenRefresh(authToken);
  }, [scheduleTokenRefresh]);

  const logout = useCallback(async () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // logoutService always clears localStorage (even if the API call fails)
    await logoutService();
    setUser(null);
    setToken(null);
    // No window.location.href — ProtectedRoute detects isAuthenticated=false
    // and redirects to /login client-side with no server request
  }, []);

  // Listen for 401 session-expired events fired by the API interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      lsRemove('auth_token');
      lsRemove('auth_refresh_token');
      lsRemove('auth_user');
      setUser(null);
      setToken(null);
      // ProtectedRoute will redirect to /login automatically
    };
    window.addEventListener('api:session-expired', handleSessionExpired);
    return () => window.removeEventListener('api:session-expired', handleSessionExpired);
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    lsSet('auth_user', JSON.stringify(updatedUser));
  }, []);

  const isAuthenticated = !!token && !!user;
  const isAdmin = user?.role === 'admin';

  // Admin users always get full Pro access — override plan in the user object
  // so every plan check in the UI (isPaid, isPro, etc.) works automatically
  const effectiveUser = isAdmin && user ? { ...user, plan: 'pro' } : user;

  return (
    <AuthContext.Provider value={{
      user: effectiveUser,
      token,
      isAuthenticated,
      isLoading,
      isAdmin,
      login,
      logout,
      updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
